<template>
  <div class="despesas-categorias-form">
    <div class="form-row">
      <div class="form-field full-width">
        <input-text
          data-testid="despesas-categorias-input-titulo"
          :label="t('Título')"
          :placeholder="t('Título')"
          :validation="() => isTituloValid"
          :required="true"
          elem-id="despesa_categoria_titulo"
          v-model="formData.despesa_categoria_titulo">
        </input-text>
      </div>
    </div>

    <div class="form-row">
      <div class="form-field full-width">
        <input-text-area
          data-testid="despesas-categorias-input-descricao"
          :label="t('Descrição')"
          :placeholder="t('Explique quando usar esta categoria')"
          :rows="3"
          elem-id="despesa_categoria_descricao"
          v-model="formData.despesa_categoria_descricao">
        </input-text-area>
      </div>
    </div>

    <div class="form-row">
      <div class="form-field half-width">
        <select-list
          data-testid="despesas-categorias-select-status"
          :label="t('Status')"
          :placeholder="t('Escolha o status')"
          :itens-select="statusOptions"
          :validation="() => true"
          :required="true"
          elem-id="despesa_categoria_status"
          v-model="formData.despesa_categoria_status">
        </select-list>
      </div>
      <div class="form-field half-width">
        <select-list
          data-testid="despesas-categorias-select-investimento"
          :label="t('Investimento')"
          :placeholder="t('Escolha')"
          :itens-select="investimentoOptions"
          :validation="() => true"
          :required="true"
          elem-id="despesa_categoria_investimento"
          v-model="formData.despesa_categoria_investimento">
        </select-list>
      </div>
    </div>

    <div class="form-row">
      <div class="form-field full-width">
        <input-number
          data-testid="despesas-categorias-input-percentual"
          :label="t('Percentual Padrão') + ' (%)'"
          :placeholder="t('0')"
          :validation="() => isPercentualValid"
          :required="true"
          elem-id="despesa_categoria_percentual_padrao"
          v-model="formData.despesa_categoria_percentual_padrao">
        </input-number>
      </div>
    </div>

    <div class="form-row">
      <div class="form-field full-width">
        <select-list
          data-testid="despesas-categorias-select-exige-nf"
          :label="t('Exige nota fiscal')"
          :placeholder="t('Escolha')"
          :itens-select="investimentoOptions"
          :validation="() => true"
          :required="true"
          elem-id="despesa_categoria_exige_nf"
          v-model="formData.despesa_categoria_exige_nf">
        </select-list>
      </div>
    </div>

    <div class="form-row">
      <div class="form-field full-width">
        <select-list
          data-testid="despesas-categorias-select-grupo-pex"
          :label="t('Grupo PEX')"
          :placeholder="t('Não conta para o PEX')"
          :itens-select="grupoPexOptions"
          :validation="() => true"
          :auto-select-first="false"
          elem-id="despesa_categoria_grupo_pex"
          v-model="formData.despesa_categoria_grupo_pex">
        </select-list>
      </div>
    </div>

    <div class="form-row">
      <div class="form-field half-width">
        <select-list
          data-testid="despesas-categorias-select-natureza"
          :label="t('Natureza')"
          :placeholder="t('Sem natureza')"
          :itens-select="naturezaOptions"
          :validation="() => true"
          :auto-select-first="false"
          elem-id="despesa_categoria_natureza"
          v-model="formData.despesa_categoria_natureza">
        </select-list>
      </div>
      <div class="form-field half-width">
        <input-text
          data-testid="despesas-categorias-input-subcategoria"
          :label="t('Subcategoria')"
          :placeholder="t('Ex.: RH, MKT, Ocupação')"
          :validation="() => true"
          elem-id="despesa_categoria_subcategoria"
          v-model="formData.despesa_categoria_subcategoria">
        </input-text>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDespesasCategorias } from '@/composables/useDespesasCategorias';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface SelectOption {
  ID: Id;
  Name: string;
}

interface Categoria {
  despesa_categoria_id: Id;
  despesa_categoria_titulo?: string;
  despesa_categoria_status?: number | string;
  despesa_categoria_investimento?: number | string;
  despesa_categoria_percentual_padrao?: number | string;
  despesa_categoria_descricao?: string | null;
  despesa_categoria_exige_nf?: number | string;
  despesa_categoria_grupo_pex?: string | null;
  despesa_categoria_natureza?: string | null;
  despesa_categoria_subcategoria?: string | null;
  [key: string]: unknown;
}

interface FormData {
  despesa_categoria_titulo: string;
  despesa_categoria_descricao: string;
  despesa_categoria_status: string;
  despesa_categoria_investimento: string;
  despesa_categoria_percentual_padrao: number;
  despesa_categoria_exige_nf: string;
  despesa_categoria_grupo_pex: string;
  despesa_categoria_natureza: string;
  despesa_categoria_subcategoria: string;
}

const props = withDefaults(defineProps<{
  isEditMode?: boolean;
  categoria?: Categoria | null;
}>(), {
  isEditMode: false,
  categoria: null
});

const { t } = useI18n();

const { createCategoria, updateCategoria } = useDespesasCategorias();

const isLoading = ref<boolean>(false);

const formData = reactive<FormData>({
  despesa_categoria_titulo: '',
  despesa_categoria_descricao: '',
  despesa_categoria_status: '1',
  despesa_categoria_investimento: '0',
  despesa_categoria_percentual_padrao: 0,
  despesa_categoria_exige_nf: '0',
  despesa_categoria_grupo_pex: '',
  despesa_categoria_natureza: '',
  despesa_categoria_subcategoria: ''
});

const statusOptions = ref<SelectOption[]>([
  { ID: '1', Name: t('Ativo') },
  { ID: '0', Name: t('Inativo') }
]);

const investimentoOptions = ref<SelectOption[]>([
  { ID: '1', Name: t('Sim') },
  { ID: '0', Name: t('Não') }
]);

// DS 2026-0075: o grupo obrigatório do DRE no PEX; vazio = a categoria não conta.
const grupoPexOptions = ref<SelectOption[]>([
  { ID: '', Name: t('Não conta para o PEX') },
  { ID: 'PRODUTOS', Name: t('Produtos') },
  { ID: 'MKT', Name: t('Marketing') },
  { ID: 'EQUIPE', Name: t('Equipe') },
  { ID: 'DESPESAS_ADM', Name: t('Despesas administrativas') }
]);

// NS 2026-0103: a natureza no Resultado Financeiro; vazio = sem natureza.
const naturezaOptions = ref<SelectOption[]>([
  { ID: '', Name: t('Sem natureza') },
  { ID: 'FIXO', Name: t('Fixa') },
  { ID: 'VARIAVEL', Name: t('Variável') }
]);

const loadForEdit = (categoria: Categoria): void => {
  log('DespesasCategoriasForm', 'loadForEdit', 'Begin', categoria);
  log('DespesasCategoriasForm', 'loadForEdit', 'Raw values', {
    status: categoria.despesa_categoria_status,
    investimento: categoria.despesa_categoria_investimento
  });

  const parsed: FormData = {
    despesa_categoria_titulo: categoria.despesa_categoria_titulo || '',
    despesa_categoria_descricao: categoria.despesa_categoria_descricao || '',
    despesa_categoria_status: String(categoria.despesa_categoria_status ?? '1'),
    despesa_categoria_investimento: String(categoria.despesa_categoria_investimento ?? '0'),
    despesa_categoria_percentual_padrao: parseFloat(String(categoria.despesa_categoria_percentual_padrao ?? 0)),
    despesa_categoria_exige_nf: String(categoria.despesa_categoria_exige_nf ?? '0'),
    despesa_categoria_grupo_pex: categoria.despesa_categoria_grupo_pex || '',
    despesa_categoria_natureza: categoria.despesa_categoria_natureza || '',
    despesa_categoria_subcategoria: categoria.despesa_categoria_subcategoria || ''
  };

  log('DespesasCategoriasForm', 'loadForEdit', 'Parsed values', parsed);
  Object.assign(formData, parsed);
  log('DespesasCategoriasForm', 'loadForEdit', 'formData after assign', { ...formData });
};

const resetForm = (): void => {
  log('DespesasCategoriasForm', 'resetForm', 'Resetting form');
  Object.assign(formData, {
    despesa_categoria_titulo: '',
    despesa_categoria_descricao: '',
    despesa_categoria_status: '1',
    despesa_categoria_investimento: '0',
    despesa_categoria_percentual_padrao: 0,
    despesa_categoria_exige_nf: '0',
    despesa_categoria_grupo_pex: '',
    despesa_categoria_natureza: '',
    despesa_categoria_subcategoria: ''
  });
};

const initializeForNew = (): void => {
  log('DespesasCategoriasForm', 'initializeForNew', 'Initializing form for new category');
  resetForm();
};

const saveCategoria = async (): Promise<void> => {
  log('DespesasCategoriasForm', 'saveCategoria', 'Attempt save', {
    isEditMode: props.isEditMode,
    formData
  });

  if (!canSave.value) {
    log('DespesasCategoriasForm', 'saveCategoria', 'Form validation failed');
    return;
  }

  isLoading.value = true;

  try {
    const dataToSave = {
      ...formData,
      despesa_categoria_status: parseInt(formData.despesa_categoria_status, 10),
      despesa_categoria_investimento: parseInt(formData.despesa_categoria_investimento, 10),
      despesa_categoria_exige_nf: parseInt(formData.despesa_categoria_exige_nf, 10)
    };

    if (props.isEditMode && props.categoria) {
      await updateCategoria(props.categoria.despesa_categoria_id, dataToSave);
      log('DespesasCategoriasForm', 'saveCategoria', 'Category updated successfully');
    } else {
      await createCategoria(dataToSave);
      log('DespesasCategoriasForm', 'saveCategoria', 'Category created successfully');
    }
  } catch (error) {
    log('DespesasCategoriasForm', 'saveCategoria', 'Error saving category', error);
    throw error;
  } finally {
    isLoading.value = false;
  }
};

const isTituloValid = computed<boolean>(() => {
  return formData.despesa_categoria_titulo.trim().length > 0;
});

const isPercentualValid = computed<boolean>(() => {
  const value = formData.despesa_categoria_percentual_padrao;
  return value >= 0 && value <= 100;
});

const canSave = computed<boolean>(() => {
  return isTituloValid.value &&
         isPercentualValid.value &&
         !isLoading.value;
});

watch(() => props.categoria, (newCategoria) => {
  if (newCategoria && props.isEditMode) {
    log('DespesasCategoriasForm', 'watch:categoria', 'Loading categoria data', newCategoria);
    loadForEdit(newCategoria);
  }
}, { immediate: true });

defineExpose({
  loadForEdit,
  initializeForNew,
  saveCategoria,
  resetForm,
  canSave
});
</script>

<style scoped>
.despesas-categorias-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 0;
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.full-width {
  flex: 1 1 100%;
}

.half-width {
  flex: 1 1 calc(50% - 8px);
  min-width: 200px;
}

@media (max-width: 768px) {
  .half-width {
    flex: 1 1 100%;
  }
}

.form-group {
  margin-bottom: 15px;
}

.text-danger {
  color: #dd4b39;
}

.input-group-addon {
  background-color: #f4f4f4;
  border: 1px solid #d2d6de;
  border-left: 0;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: #555;
  text-align: center;
}

.is-invalid {
  border-color: #dd4b39;
}

.invalid-feedback {
  color: #dd4b39;
  font-size: 12px;
  margin-top: 5px;
}
</style>
